import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";

interface PushSubscriptionState {
  isSupported: boolean;
  isSubscribed: boolean;
  permission: NotificationPermission | "default";
  loading: boolean;
}

const NOTIFICATION_STATUS_KEY = "notification_subscription_status";

export function usePushNotifications() {
  const { user } = useAuth();
  const [state, setState] = useState<PushSubscriptionState>({
    isSupported: false,
    isSubscribed: false,
    permission: "default",
    loading: true,
  });

  // Refresh permission state (useful after user changes browser settings)
  const refreshPermission = useCallback(() => {
    if ("Notification" in window) {
      const permission = Notification.permission;
      setState((prev) => ({ ...prev, permission }));
      return permission;
    }
    return "default" as NotificationPermission;
  }, []);

  useEffect(() => {
    const checkSupport = async () => {
      const isSupported = "Notification" in window && "serviceWorker" in navigator;
      
      // Note: PushManager is not available on iOS Safari, but we can still use local notifications
      const hasPushManager = "PushManager" in window;

      if (!isSupported) {
        setState({
          isSupported: false,
          isSubscribed: false,
          permission: "default",
          loading: false,
        });
        return;
      }

      const permission = Notification.permission;

      // Check localStorage for subscription status (more reliable fallback)
      const storedSubscription = localStorage.getItem(NOTIFICATION_STATUS_KEY);
      
      // Check if already subscribed via PushManager
      let isSubscribed = false;
      if (hasPushManager) {
        try {
          const registration = await navigator.serviceWorker.ready;
          const subscription = await registration.pushManager.getSubscription();
          isSubscribed = !!subscription;
        } catch {
          // Ignore errors - might not have service worker ready yet
        }
      }
      
      // If granted permission and stored as subscribed, consider subscribed
      if (permission === "granted" && storedSubscription === "true") {
        isSubscribed = true;
      }

      setState({
        isSupported: true,
        isSubscribed,
        permission,
        loading: false,
      });
    };

    checkSupport();

    // Re-check permission when page becomes visible (user might have changed settings)
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshPermission();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refreshPermission]);

  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!state.isSupported) return false;

    try {
      const permission = await Notification.requestPermission();
      setState((prev) => ({ ...prev, permission }));
      return permission === "granted";
    } catch (error) {
      console.error("Error requesting notification permission:", error);
      return false;
    }
  }, [state.isSupported]);

  const subscribe = useCallback(async (): Promise<boolean> => {
    if (!state.isSupported) return false;

    try {
      // First request permission if not granted
      if (state.permission !== "granted") {
        const granted = await requestPermission();
        if (!granted) {
          // Refresh to get current state after user interaction
          refreshPermission();
          return false;
        }
      }

      const hasPushManager = "PushManager" in window;
      
      if (hasPushManager) {
        try {
          const registration = await navigator.serviceWorker.ready;

          // For demo purposes, we'll use a placeholder VAPID key
          // In production, you would get this from your backend
          const vapidPublicKey = "BEl62iUYgUivxIkv69yViEuiBIa-Ib9-SkvMeAtA3LFgDzkrxZJjSgSnfckjBJuBkr3qBUYIHBQFLXYp5Nksh8U";

          const subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vapidPublicKey) as BufferSource,
          });

          // Store subscription in localStorage
          localStorage.setItem("push_subscription", JSON.stringify(subscription.toJSON()));
        } catch (error) {
          console.warn("PushManager subscription failed, using fallback:", error);
          // Continue with local notification support only
        }
      }

      // Mark as subscribed in localStorage (works for all platforms)
      localStorage.setItem(NOTIFICATION_STATUS_KEY, "true");
      setState((prev) => ({ ...prev, isSubscribed: true }));
      return true;
    } catch (error) {
      console.error("Error subscribing to notifications:", error);
      return false;
    }
  }, [state.isSupported, state.permission, requestPermission, refreshPermission]);

  const unsubscribe = useCallback(async (): Promise<boolean> => {
    if (!state.isSupported) return false;

    try {
      if ("PushManager" in window) {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();

        if (subscription) {
          await subscription.unsubscribe();
        }
      }
      
      localStorage.removeItem("push_subscription");
      localStorage.removeItem(NOTIFICATION_STATUS_KEY);

      setState((prev) => ({ ...prev, isSubscribed: false }));
      return true;
    } catch (error) {
      console.error("Error unsubscribing from push notifications:", error);
      return false;
    }
  }, [state.isSupported]);

  const showNotification = useCallback(
    async (title: string, options?: NotificationOptions): Promise<boolean> => {
      if (!state.isSupported || state.permission !== "granted") return false;

      try {
        // Try service worker notification first (works in background)
        if ("serviceWorker" in navigator) {
          const registration = await navigator.serviceWorker.ready;
          await registration.showNotification(title, {
            icon: "/pwa-192x192.png",
            badge: "/pwa-192x192.png",
            ...options,
          });
          return true;
        }
        
        // Fallback to regular Notification API
        new Notification(title, {
          icon: "/pwa-192x192.png",
          ...options,
        });
        return true;
      } catch (error) {
        console.error("Error showing notification:", error);
        return false;
      }
    },
    [state.isSupported, state.permission]
  );

  // Check if notifications can be enabled (supported and not permanently denied)
  const canRequestPermission = state.isSupported && state.permission !== "denied";

  // Open browser settings instructions for denied permissions
  const getUnblockInstructions = useCallback((): string => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);
    const isChrome = /Chrome/.test(navigator.userAgent) && !/Edge|Edg/.test(navigator.userAgent);
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);

    if (isIOS) {
      return "Abra Ajustes > Safari > Notificações e permita para este site.";
    }
    if (isAndroid && isChrome) {
      return "Toque no cadeado 🔒 na barra de endereço > Permissões > Notificações > Permitir.";
    }
    if (isChrome) {
      return "Clique no cadeado 🔒 na barra de endereço > Permissões do site > Notificações > Permitir.";
    }
    if (isSafari) {
      return "Abra Safari > Preferências > Websites > Notificações e permita para este site.";
    }
    return "Acesse as configurações do navegador e permita notificações para este site.";
  }, []);

  return {
    ...state,
    canRequestPermission,
    requestPermission,
    subscribe,
    unsubscribe,
    showNotification,
    refreshPermission,
    getUnblockInstructions,
  };
}

// Helper function to convert VAPID key
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
